import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { liveClock } from '../lib/liveClock';
import { Sparkles, Calendar, MessageSquarePlus, MapPin, Scale, HandHeart, FileText, ArrowRight } from 'lucide-react';
import { renfeAPI, deportesAPI, torrebusAPI, dashboardAPI, residuosAPI } from '../lib/api';
import { getLocation, subscribeLocation, movedEnough } from '../lib/geo';
import { BARRIO_CENTERS } from '../lib/constants';
import { gtfsTime, numEs } from '../lib/utils';

import {
  ModernHero,
  RiverAlertBanner,
  QuickActions,
  RadiografiaWidget,
  KarmaLeaderboard,
  TuBarrioSection,
  EstaSemanaSection,
  BoletinWidget,
  AvisosSection,
} from '../components/dashboard';
import useLatestRequest from '../lib/useLatestRequest';
import { isSupermarket } from '../lib/supermarkets';
import IncidenciasCard, { AyudaCard } from '../components/dashboard/IncidenciasCard';
import PlazosPronto from '../components/PlazosPronto';

function getUpcomingDepartures(departures) {
  if (!departures?.length) return [];
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  return departures
    .filter(d => {
      const [h, m] = d.departure.split(':').map(Number);
      return h * 60 + m > nowMins;
    })
    .slice(0, 3);
}

/** When a fixture is, short enough to sit at the end of a row: "hoy 19:00", "sáb 26 · 19:00".
 *  Europe/Madrid explicitly — the kick-off is local, and a phone set to another zone should not
 *  quietly shift it to the day before. */
function fixtureWhen(match) {
  const time = match.hora || '';
  if (!match.fecha) return time;
  try {
    const madrid = { timeZone: 'Europe/Madrid' };
    const today = new Date().toLocaleDateString('en-CA', madrid);           // YYYY-MM-DD
    if (match.fecha === today) return time ? `hoy ${time}` : 'hoy';
    const d = new Date(`${match.fecha}T${time || '00:00'}:00`);
    if (Number.isNaN(d.getTime())) return time;
    const day = d.toLocaleDateString('es-ES', { ...madrid, weekday: 'short', day: 'numeric' });
    return time ? `${day} · ${time}` : day;
  } catch {
    return time;
  }
}

// The nearest stop and bins are asked for again only after a move this large (50 m): GPS
// refinements of a few metres used to repeat both requests each time.
const NEAREST_REFETCH_KM = 0.05;

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// index.js starts the dashboard request before React mounts; older than this, it is no longer "now".
const PRELOAD_MAX_AGE_MS = 60000;

export default function Dashboard({ user, onShowAuth }) {
  const navigate = useNavigate();

  // ── State ──
  const [weather, setWeather] = useState(null);
  const [stats, setStats] = useState(null);
  const [airQuality, setAirQuality] = useState(null);
  const [construction, setConstruction] = useState([]);
  const [liveScores, setLiveScores] = useState(null);
  const [sportsFixtures, setSportsFixtures] = useState(null);
  const [renfeDepartures, setRenfeDepartures] = useState([]);
  const [bikeStationCount, setBikeStationCount] = useState(0);
  const [forecast, setForecast] = useState([]);
  const [allBulletins, setAllBulletins] = useState([]);
  const [pollen, setPollen] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [nearestBusStop, setNearestBusStop] = useState(null);
  const [agendaEvents, setAgendaEvents] = useState([]);
  const [nearestBusArrivals, setNearestBusArrivals] = useState([]);
  const [liveTrains, setLiveTrains] = useState([]);
  const [nextBusSantander, setNextBusSantander] = useState(null);
  const [nearestBins, setNearestBins] = useState(null);
  const [recentIncidencias, setRecentIncidencias] = useState([]);
  const [incidenciasCount, setIncidenciasCount] = useState(0);
  const [incidenciasResolvedCount, setIncidenciasResolvedCount] = useState(0);
  const [riverLevels, setRiverLevels] = useState(null);
  const [embalses, setEmbalses] = useState(null);
  const [farmaciaGuardia, setFarmaciaGuardia] = useState(null);
  const [farmaciasData, setFarmaciasData] = useState(null);
  const [allSupermarkets, setAllSupermarkets] = useState([]);
  const [riverAlerts, setRiverAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('somostorre_dismissed_river_alerts') || '[]'); } catch { return []; }
  });
  const [cityStats, setCityStats] = useState(null);
  const [bikeStations, setBikeStations] = useState([]);
  const [freshness, setFreshness] = useState({});
  const [weatherEnhanced, setWeatherEnhanced] = useState({});
  const [weatherObservation, setWeatherObservation] = useState(null);
  const [nextFestivo, setNextFestivo] = useState(null);
  const [eraLive, setEraLive] = useState(null);
  const [karmaLeaderboard, setKarmaLeaderboard] = useState([]);
  const [karmaTotals, setKarmaTotals] = useState(null);
  const [uvRealtime, setUvRealtime] = useState(null);
  const [ayudasAbiertas, setAyudasAbiertas] = useState(0);
  const [latestContract, setLatestContract] = useState(null);
  const [latestHelpRequest, setLatestHelpRequest] = useState(null);
  const [myHelpActivity, setMyHelpActivity] = useState(null); // User's own request with activity

  // ── Poll results that have not changed should not cause a render ──
  //
  // fetchLiveTrains runs every 20 seconds and called setLiveTrains(res.data || []) every time —
  // a new array identity per tick whether or not a train had moved. React compares state by
  // identity, so that re-rendered this component and all ~18 widgets under it. Live scores (30s)
  // and nearest-stop arrivals (30s) did the same, which came to roughly three full dashboard
  // re-renders a minute with nothing on screen actually changing.
  //
  // This is deliberately the conservative half of the fix. It skips a render only when the new
  // payload is byte-identical to the last one, so anything that genuinely changed still renders
  // exactly as before. Unlike React.memo it cannot leave a stale screen: it compares the data
  // rather than trusting that a reference standing still means the contents did.
  const seenPayloads = useRef({});
  const setIfChanged = useCallback((key, value, setter) => {
    let serialised;
    try {
      serialised = JSON.stringify(value);
    } catch {
      setter(value); // unserialisable (cycle) — never skip, just set
      return;
    }
    if (seenPayloads.current[key] === serialised) return;
    seenPayloads.current[key] = serialised;
    setter(value);
  }, []);

  // ── Data fetching callbacks ──
  // Both are polled. A slow poll overtaken by the next one must not put older data back.
  const latestScores = useLatestRequest();
  const latestTrains = useLatestRequest();
  const fetchLiveScores = useCallback(async () => {
    const req = latestScores();
    try {
      const res = await deportesAPI.liveScores();
      if (req.current()) setIfChanged('liveScores', res.data, setLiveScores);
    } catch (e) { /* error logged */ }
  }, [setIfChanged, latestScores]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLiveTrains = useCallback(() => {
    // Only fetch GTFS-RT positions (lightweight, needs polling)
    const req = latestTrains();
    renfeAPI.live().then(res => { if (req.current()) setIfChanged('liveTrains', res.data || [], setLiveTrains); }).catch(() => {});
  }, [setIfChanged, latestTrains]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Main data fetch — single combined endpoint ──
  const lastDashboardRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      try {
        // Use preloaded data if available (started in index.js before React mounted)
        let res;
        const preload = window.__dashboardPreload;
        window.__dashboardPreload = null; // Use only once
        if (preload && Date.now() - preload.at < PRELOAD_MAX_AGE_MS) {
          res = await preload.promise;
        }
        if (!res) res = await dashboardAPI.getAll();
        const d = res?.data;
        if (!d) return;
        
        // Skip re-render if data hasn't changed (tab switch without new data)
        const fingerprint = `${d._freshness?.weather?.age_seconds || 0}-${d.liveScores?.timestamp || ''}-${d.vecinosCount || 0}-${d.sportsFixtures?.partidos?.length || 0}-${d.eraLive?.total_available || 0}`;
        if (lastDashboardRef.current === fingerprint) return;
        lastDashboardRef.current = fingerprint;
          setWeather(d.weather?.fallback || d.weather);
          setStats(d.stats);
          setConstruction(d.construction || []);
          setAirQuality(d.airQuality);
          setForecast(d.forecast?.days || []);
          setPollen(d.pollen);
          setRiverLevels(d.riverLevels);
          setEmbalses(d.embalses || null);
          setRiverAlerts(d.riverAlerts?.alerts || []);
          setFarmaciaGuardia(d.farmaciaGuardia);
          setAllBulletins(d.bulletins || []);
          setRecentIncidencias(d.incidencias || []);
          setIncidenciasCount(d.incidencias_count || (d.incidencias || []).length);
          setIncidenciasResolvedCount(d.incidencias_resolved_count || 0);
          // Through setIfChanged, not setLiveScores directly. The 30s poller writes this state
          // through the guard, and the guard remembers the last payload it wrote. A direct write
          // here left that memory stale, so the very next poll could compare against a value it
          // had not actually written and skip a correction the poller needed to make.
          setIfChanged('liveScores', d.liveScores, setLiveScores);
          setSportsFixtures(d.sportsFixtures);
          setFreshness(d._freshness || {});
          setWeatherEnhanced(d.weatherEnhanced || {});
          setNextFestivo(d.nextFestivo || null);
          setEraLive(d.eraLive || null);
          setKarmaLeaderboard(d.karmaLeaderboard || []);
          setKarmaTotals(d.karmaTotals || null);
          setUvRealtime(d.uvRealtime || null);
          setAyudasAbiertas(d.ayudasAbiertas || 0);
          if (d.latestContract) setLatestContract(d.latestContract);
          if (d.latestHelpRequest) setLatestHelpRequest(d.latestHelpRequest);

          // Check user's own help requests for activity (candidato offers, messages)
          if (user) {
            import('../lib/api').then(({ vecinosAPI }) => {
              vecinosAPI.list({ estado: 'abierto' }).then(res => {
                // The list strips candidato_id / candidato_nombre and says has_candidato instead
                // (a pending offer; accepting one clears it). The detail gives the author the name.
                const mine = (res.data?.requests || res.data || []).filter(r => r.autor_id === user.id && r.has_candidato);
                if (mine.length === 0) return;
                setMyHelpActivity(mine[0]);
                vecinosAPI.detail(mine[0].id).then(({ data: req }) => {
                  if (req?.candidato_nombre) setMyHelpActivity(prev => (prev?.id === req.id ? { ...prev, candidato_nombre: req.candidato_nombre } : prev));
                }).catch(() => {});
              }).catch(() => {});
            });
          }

          // Data now included in combined endpoint (no separate calls needed)
          if (d.bikeStations) { setBikeStationCount(d.bikeStations.length); setBikeStations(d.bikeStations); }
          if (d.agendaEvents) setAgendaEvents(d.agendaEvents);

          // Supermarkets
          // The dashboard sends all of «supermercado» and «alimentación»; the same rule as the
          // Supermercados page keeps the supermarkets (not the butcher round the corner).
          if (d.supermarkets && d.supermarkets.length > 0) {
            setAllSupermarkets(d.supermarkets.filter(isSupermarket));
          }

          // Vecinos count for hero section
          if (d.vecinosCount) {
            setStats(prev => ({ ...(prev || {}), vecinos: d.vecinosCount }));
          }

          // Estadisticas → cityStats (backend now sends pre-extracted values)
          if (d.stats) setCityStats(d.stats);

          // New combined fields — observation, transport, farmacias
          if (d.observation) setWeatherObservation(d.observation);
          if (d.renfeDepartures) setRenfeDepartures(d.renfeDepartures);
          if (d.busNextSantander) setNextBusSantander(d.busNextSantander);
          if (d.farmacias) setFarmaciasData(d.farmacias);
      } catch (e) {
        /* error logged */
      }
    }
    fetchData();

    // Only fetch live train positions separately (needs frequent polling)
    fetchLiveTrains();

    // Refresh dashboard data when user returns to the app — throttled to max once per 60s
    let lastRefresh = Date.now();
    const onDashboardVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh > 60000) {
        lastRefresh = Date.now();
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', onDashboardVisible);
    return () => document.removeEventListener('visibilitychange', onDashboardVisible);
  }, [fetchLiveTrains]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Polling intervals — train departures always, live scores smart polling ──
  const fixturesRef = useRef(sportsFixtures);
  fixturesRef.current = sportsFixtures;

  useEffect(() => {
    // Poll only while the page is visible. Hidden (background tab, app in background) the
    // intervals are cleared; on return everything is fetched once and the intervals restart.
    const hasLive = liveScores?.hay_en_vivo;

    // Smart live score polling: check if any fixture is starting soon
    const isMatchImminent = () => {
      if (hasLive) return true;
      const fixtures = fixturesRef.current?.partidos || [];
      if (!fixtures.length) return false;
      const now = new Date();
      for (const f of fixtures) {
        if (!f.fecha || !f.hora) continue;
        try {
          const matchTime = new Date(`${f.fecha}T${f.hora}:00`);
          const diffMin = (matchTime - now) / 60000;
          // Start polling 10 min before kickoff and for 3 hours after
          if (diffMin > -180 && diffMin < 10) return true;
        } catch { /* ignore parse errors */ }
      }
      return false;
    };

    let trainInterval = null;
    let scoreInterval = null;
    const stop = () => {
      clearInterval(trainInterval);
      clearInterval(scoreInterval);
      trainInterval = null;
      scoreInterval = null;
    };
    const start = () => {
      stop();
      trainInterval = setInterval(fetchLiveTrains, 20000);
      // 30s when live, 60s when match imminent, no polling otherwise
      if (isMatchImminent()) scoreInterval = setInterval(fetchLiveScores, hasLive ? 30000 : 60000);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchLiveScores();
        fetchLiveTrains();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchLiveScores, fetchLiveTrains, liveScores?.hay_en_vivo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GPS ──
  // Cached fetch (≤2min) + live refinement subscription via the shared geo helper.
  useEffect(() => {
    let cancelled = false;
    getLocation().then((loc) => {
      if (!cancelled && loc?.lat && loc?.lon) setUserLocation(loc);
    });
    const unsubLoc = subscribeLocation((loc) => {
      if (!cancelled && loc?.lat && loc?.lon) setUserLocation(loc);
    });
    return () => { cancelled = true; unsubLoc(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Where «Tu barrio» is ──
  // The reader's position when we have it. Without it (denied, off, or not yet arrived) the centre
  // of their profile barrio, or Centro, marked approximate: TuBarrioSection then says so and shows
  // no walking times, since those would be from a point the reader may not be at. Agreed with
  // Tanuj on 2026-09-24; before this the section showed only an "Activa tu ubicación" card.
  const placeLocation = useMemo(() => {
    if (userLocation) return userLocation;
    const barrio = BARRIO_CENTERS[user?.barrio] ? user.barrio : 'Centro';
    const [lat, lon] = BARRIO_CENTERS[barrio];
    return { lat, lon, approximate: true, barrio };
  }, [userLocation, user?.barrio]);

  // ── Nearest bus stop + bins (server-side) ──
  // GPS pushes arrive one after another; an answer for an earlier position must not replace the
  // nearest stop and bins for where the reader is now. Each refinement used to ask both again:
  // now only a move of NEAREST_REFETCH_KM from the position last asked about (or the switch from
  // the barrio's centre to a real fix) does. The sequence number, not the effect's cleanup, drops
  // stale answers, so a skipped refinement does not discard the answer still on its way.
  const nearestReq = useRef({ pos: null, seq: 0 });
  // Unmount (StrictMode's too): later answers are dropped, and a remount asks again.
  useEffect(() => () => { nearestReq.current = { pos: null, seq: nearestReq.current.seq + 1 }; }, []);
  useEffect(() => {
    if (!movedEnough(nearestReq.current.pos, placeLocation, NEAREST_REFETCH_KM)) return;
    const mine = ++nearestReq.current.seq;
    nearestReq.current.pos = placeLocation;
    const current = () => mine === nearestReq.current.seq;
    torrebusAPI.nearest(placeLocation.lat, placeLocation.lon)
      .then(res => { if (current() && res.data?.stop) setNearestBusStop(res.data.stop); })
      .catch(() => {});
    residuosAPI.nearest(placeLocation.lat, placeLocation.lon)
      .then(res => { if (current() && res.data?.bins) setNearestBins(res.data.bins); })
      .catch(() => {});
  }, [placeLocation]);

  // ── Nearest bike station ──
  const nearestBike = useMemo(() => {
    if (!placeLocation || !bikeStations.length) return null;
    let best = null, bestDist = Infinity;
    for (const s of bikeStations) {
      const d = haversineKm(placeLocation.lat, placeLocation.lon, s.lat, s.lon);
      if (d < bestDist) { bestDist = d; best = s; }
    }
    return best ? { ...best, walkMin: Math.max(1, Math.round(bestDist * 1000 / 80)) } : null;
  }, [placeLocation, bikeStations]);

  // Nearest pharmacy: TuBarrioSection works it out from farmaciasData (the full list, with
  // addresses). The dashboard API no longer sends a farmaciasNearby list.

  // ── Nearest supermarket ──
  const nearestSupermarket = useMemo(() => {
    if (!placeLocation || !allSupermarkets.length) return null;
    let best = null, bestDist = Infinity;
    for (const s of allSupermarkets) {
      const d = haversineKm(placeLocation.lat, placeLocation.lon, s.lat, s.lon);
      if (d < bestDist) { bestDist = d; best = s; }
    }
    return best ? { ...best, distKm: bestDist, walkMin: Math.max(1, Math.round(bestDist * 1000 / 80)) } : null;
  }, [placeLocation, allSupermarkets]);

  // ── Live arrivals for nearest bus stop (stale-while-revalidate) ──
  // Keyed on the stop's code: the same stop in a new object (each /nearest answer) is not a new
  // stop, and restarting the 30 s poll for it fetched its arrivals again.
  const nearestStopCode = nearestBusStop?.codigo;
  useEffect(() => {
    if (!nearestStopCode) return;
    // Each 30 s poll only counts if it is the newest for THIS stop. When the nearest stop changes,
    // the previous stop's answers (and its background live refresh) are dropped, not shown under
    // the new stop's name; a slow poll overtaken by the next one is dropped too.
    let alive = true; let seq = 0;
    const fetchArrivals = () => {
      const mine = ++seq;
      const ok = () => alive && mine === seq;
      torrebusAPI.arrivals(nearestStopCode).then(res => {
        if (!ok()) return;
        setIfChanged('busArrivals', (res.data?.arrivals || []).slice(0, 2), setNearestBusArrivals);
        if (res.data?.from_cache) {
          torrebusAPI.arrivalsLive(nearestStopCode)
            .then(live => { if (ok() && live.data?.arrivals) setIfChanged('busArrivals', live.data.arrivals.slice(0, 2), setNearestBusArrivals); })
            .catch(() => {});
        }
      }).catch(() => {});
    };
    fetchArrivals();
    // No polling while hidden; catch up as soon as the page is visible again.
    const interval = setInterval(() => { if (document.visibilityState !== 'hidden') fetchArrivals(); }, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchArrivals(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [nearestStopCode, setIfChanged]);

  // ── Derived data ──
  // The live feed (/deportes/live) names things differently from the fixtures the card was written
  // for: local and visitante are team names and somos_local says which one is ours, where a
  // fixture has equipo, rival and a boolean local. Read as a fixture, a live match showed blank
  // names («vs») and «LOCAL» even away. Mapped here to the card's shape.
  const liveMatches = (liveScores?.partidos || [])
    .filter(m => m.en_vivo || m.finalizado_hoy || m.reciente)
    .map(m => ({
      ...m,
      id: m.game_id ?? m.id,
      equipo: m.equipo_nombre || (m.somos_local ? m.local : m.visitante),
      rival: m.somos_local ? m.visitante : m.local,
      local: !!m.somos_local,
    }));
  // A match in progress: Próximos partidos moves above Tu ciudad until it ends; the rest of the week
  // the civic part leads (Tanuj, 2026-09-25).
  const matchLive = liveMatches.some(m => m.en_vivo);
  // Show 1 upcoming match per sport (balonmano, baloncesto, fútbol)
  const upcomingFixtures = (() => {
    const all = (sportsFixtures?.partidos || []).filter(m => m.programado || (!m.en_vivo && !m.finalizado));
    const seen = new Set();
    return all.filter(m => {
      const sport = m.deporte || '';
      if (seen.has(sport)) return false;
      seen.add(sport);
      return true;
    }).slice(0, 3);
  })();
  // NOT memoised, deliberately. getUpcomingDepartures reads new Date() (line 32) and filters
  // departures against "now", so it is not a pure function of its argument. Memoising it on
  // [renfeDepartures] pinned the result to the clock at the last data change — and
  // renfeDepartures is only written by the /api/dashboard fetch, which has no interval. The
  // train card froze: open Inicio at 08:55 showing "09:02 to Santander" and at 09:40 it still
  // said 09:02, a departed train presented as the next one. setIfChanged made it worse by
  // removing the 20s poller render that used to recompute this by accident.
  //
  // Recomputing per render is the correct behaviour for a clock-dependent value. The wasted
  // array is nothing next to showing a time that has already gone.
  const upcoming = getUpcomingDepartures(renfeDepartures);

  // ── Render ──
  // Próximos partidos — its own act again, and higher.
  // Deportes used to sit here as a horizontal strip of fixture cards; part 2 of the Inicio
  // rework moved the whole block to /deportes and left liveMatches and upcomingFixtures
  // being computed on every render for nobody. Folding them into a row inside "Hoy" put
  // them 2.300px down and stripped the shape that made them scannable — which sport, who
  // is at home, what time. Same card as before, one fixture per sport, and a live score
  // the moment there is one.
  const partidosSection = (liveMatches.length > 0 || upcomingFixtures.length > 0) && (
    <section className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 mb-4 border border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.06)] overflow-hidden"
      data-testid="partidos-section" aria-labelledby="home-partidos">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base sm:text-lg" aria-hidden="true">⚽</span>
          <h2 id="home-partidos" className="text-sm sm:text-base font-extrabold text-slate-900">
            {/* «en vivo» only while a match is being played; a finished one is a result. */}
            {liveMatches.some((m) => m.en_vivo) ? 'Deporte en vivo' : liveMatches.length > 0 ? 'Últimos resultados' : 'Próximos partidos'}
          </h2>
          {liveMatches.some((m) => m.en_vivo) && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200/50">
              <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span></span>
              <span className="text-[11px] font-bold text-red-600">EN VIVO</span>
            </span>
          )}
        </div>
        <button onClick={() => navigate('/deportes')} className="text-[11px] text-torre-teal font-bold hover:underline flex items-center gap-0.5 shrink-0" data-testid="partidos-see-all">
          Ver todo <ArrowRight size={11} />
        </button>
      </div>
      {/* A swipeable row on a phone; from sm the cards share the width (they were fixed at
          210px, leaving half the section empty at 1440px). */}
      <div className="flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide sm:grid sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] sm:overflow-visible" data-testid="partidos-list">
        {(liveMatches.length > 0 ? liveMatches : upcomingFixtures).slice(0, 4).map((p, i) => {
          const sportEmoji = { balonmano: '🤾', baloncesto: '🏀', 'fútbol': '⚽' }[p.deporte] || '⚽';
          return (
            <button key={p.id || i} onClick={() => navigate('/deportes')}
              className="min-w-[170px] flex-1 sm:min-w-0 p-2.5 sm:p-3 rounded-xl bg-slate-50 border border-slate-100 shrink-0 text-left hover:bg-slate-100 transition-colors"
              data-testid={`partido-${i + 1}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-sm" aria-hidden="true">{sportEmoji}</span>
                <p className="text-[11px] sm:text-xs text-slate-400 truncate">{p.liga}</p>
              </div>
              {/* The local side reads first and in teal, home or away, so a glance answers
                  "are we playing at home" without parsing the fixture. */}
              {p.local === false ? (
                <>
                  <p className="text-xs sm:text-sm font-bold text-slate-900 truncate">{p.rival}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">vs <span className="font-semibold text-torre-teal">{p.equipo}</span></p>
                </>
              ) : (
                <>
                  <p className="text-xs sm:text-sm font-bold text-torre-teal truncate">{p.equipo}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">vs <span className="font-semibold text-slate-700">{p.rival}</span></p>
                </>
              )}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {p.marcador ? (
                  <>
                    <span className="text-xs font-extrabold text-slate-900 tabular-nums">{p.marcador}</span>
                    {p.en_vivo
                      ? <span className="text-[11px] font-bold text-red-600">{liveClock(p) || 'En juego'}</span>
                      : p.finalizado && <span className="text-[11px] font-bold text-slate-500">Final</span>}
                  </>
                ) : (
                  <span className="text-[11px] font-bold text-torre-teal">{fixtureWhen(p)}</span>
                )}
                {p.local === false
                  ? <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">FUERA</span>
                  : <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 font-bold">LOCAL</span>}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className="pb-4 lg:pb-8 bg-slate-50 overflow-x-hidden" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        {/* River alerts */}
        <RiverAlertBanner
          riverAlerts={riverAlerts}
          dismissedAlerts={dismissedAlerts}
          setDismissedAlerts={setDismissedAlerts}
        />

        {/* Modern Hero with data focus */}
        <ModernHero 
          karmaTotals={karmaTotals}
          nextFestivo={nextFestivo}
          weather={weather}
          airQuality={airQuality}
          pollen={pollen}
          riverLevels={riverLevels}
          embalses={embalses}
          construction={construction}
          stats={stats}
          user={user}
          freshness={freshness}
          eraLive={eraLive}
          weatherEnhanced={weatherEnhanced}
          weatherObservation={weatherObservation}
          newBoletinesCount={allBulletins.filter(b => b.fecha === new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' })).length}
          todayEventsCount={agendaEvents.filter(e => {
            const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
            return e.fecha <= today && (e.fecha_fin || e.fecha) >= today;
          }).length}
          onShowAuth={onShowAuth}
          forecast={forecast}
          uvRealtime={uvRealtime}
          ayudasAbiertas={ayudasAbiertas}
          ayudasInCard={!myHelpActivity && !!latestHelpRequest}
        />

        {/* Avisos (Bulletins) — time-sensitive info goes before any promo */}
        <AvisosSection bulletins={allBulletins} obras={construction} navigate={navigate} />

        {/* Deportes lived here as a full match-by-match block. It has its own page, its own
            entry under "Qué hacer", and a notification category that is off by default -- which
            is the honest signal about how many people want it on the front door. */}

        {/* Help Request — personal activity is an alert, stays full-width above the grid */}
        {myHelpActivity && (
          <button onClick={() => navigate(`/vecinos/${myHelpActivity.id}`)}
            className="w-full mb-3 p-4 rounded-xl border-2 border-amber-400 bg-amber-50 flex items-center gap-3 hover:shadow-sm transition-all text-left"
            data-testid="help-request-banner">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
              <HandHeart size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-amber-600 uppercase tracking-wide mb-0.5">¡Alguien quiere ayudarte!</p>
              <p className="text-sm font-bold text-torre-slate truncate">{myHelpActivity.titulo}</p>
              <p className="text-[11px] text-amber-600 truncate">{myHelpActivity.candidato_nombre || 'Un vecino'} se ofrece a ayudar</p>
            </div>
            <span className="text-[11px] font-bold text-white bg-amber-500 px-2 py-1 rounded-full shrink-0 animate-pulse">Responde</span>
          </button>
        )}

        {/* ── Order of the page ──────────────────────────────────────────────────────────
            Hero · Avisos · Tu ciudad · Próximos partidos · Torre IA · Accesos · Tu barrio ·
            Transparencia · Hoy en Torrelavega.

            Measured before any of this moved, at 1440px on a 3.817px page: the launcher started
            at 3.294px, "Únete" at 3.760px and the próximo festivo banner at 3.916px — the three
            things asking a visitor to do something all sat below the footer fold.

            What changed and why: Tu ciudad leads because what the city needs is the reason this
            app exists, and it carries Top Karma, which is the answer to "why would I". Partidos
            follows as the one thing on this page with a kick-off time. Torre IA sits after them
            rather than under the greeting: an assistant is more use once you have seen what there
            is to ask about. Accesos then catches anyone who did not find what they came for. Tu
            barrio, Transparencia and Hoy are the long reads — worth finding, nobody's first
            question.

            While a match is in progress (en_vivo), Próximos partidos moves above Tu ciudad and
            comes back here when it ends (Tanuj, 2026-09-25): a live score is the one thing on the
            page that will not wait.

            Two blocks left entirely. The bottom "Únete a la comunidad" strip was a second copy of
            the CTA inside Top Karma, which now sits near the top; moving it up would have put the
            two side by side. The festivo banner is a hero pill.
        */}
        {matchLive && partidosSection}

        {/* Tu ciudad — incidencias + ayuda vecinal side by side, contratación full-width below */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 mb-4 border border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.06)]" data-testid="tu-ciudad-section">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <span className="text-base sm:text-lg">🏛️</span>
            <span className="text-sm sm:text-base font-extrabold text-slate-900">Tu ciudad</span>
          </div>
          {/* What the city asks of you, first: the periods that close soon (taxes within 15 days, job and grant calls
              within 10), read from the BOC. The only item here with a deadline, and nothing at all when none closes
              soon, so it costs no space most days (Tanuj, 8 Oct 2026: it sat ~1,460 px down on a phone, under the
              karma ranking). The full list is Boletines → Plazos. */}
          <PlazosPronto />
          {/* From lg, two columns: the two actions on the left, Top Karma beside them. Full width,
              the leaderboard ran 1.180px with the names at one end and the points at the other,
              and the two action cards were thin strips (Tanuj, 2026-09-25: "a lot of empty
              space"). The incidencias card lists a few more reports there, where it has room. */}
          <div className="lg:grid lg:grid-cols-2 lg:gap-3 lg:items-start">
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 items-start" data-testid="tu-ciudad-cards">
            <IncidenciasCard incidencias={recentIncidencias} pendientes={incidenciasCount} resueltas={incidenciasResolvedCount}
              compact={!myHelpActivity && !!latestHelpRequest}
              className={`${(!myHelpActivity && latestHelpRequest) ? '' : 'col-span-2'} lg:col-span-1`} />
            {!myHelpActivity && latestHelpRequest && (
              <AyudaCard request={latestHelpRequest} abiertas={ayudasAbiertas}
                className={`${recentIncidencias.length > 0 ? '' : 'col-span-2'} lg:col-span-1`} />
            )}
          </div>
          {/* The reward sits with what it rewards. Top Karma used to render inside the hero,
              half a page above Reportar and Vecinos, where a three-name leaderboard on a
              municipal app reads as vanity. Directly under the two actions it scores, it reads
              as the other half of a loop. */}
          <div className="lg:[&>*]:mt-0">
            <KarmaLeaderboard karmaLeaderboard={karmaLeaderboard} karmaTotals={karmaTotals} user={user} onShowAuth={onShowAuth} />
          </div>
          </div>
        </div>

        {!matchLive && partidosSection}

        {/* Torre IA — act 1's action, directly under the greeting.
            It sat below Tu ciudad and Transparencia. Moved up, it is the first thing the page
            offers rather than the sixth.

            I repainted it teal on the argument that it was the only non-teal block in the app --
            wrong call, reverted. Torre IA's violet is not a brand inconsistency, it is the
            assistant's identity, and people recognise it. What was actually wrong is that the
            four surfaces opening Torre IA wore three different gradients between them and none
            matched the assistant itself. They all carry indigo-600 -> violet-600 now, the same
            as the chat header and the bubble, so the thing you tap looks like the thing that
            opens. */}
        {/* A div, not a button: the question chips inside are buttons of their own, and a button
            cannot contain another. The header row is the keyboard target; a click anywhere else on
            the card opens Torre IA too. */}
        <div
          onClick={() => {
            // Was: find the floating bubble and click it. The bubble is conditional, so this
            // did nothing while the cookie banner was up, during onboarding, or on /mapa.
            window.__torreOpen?.();
          }}
          className="on-dark w-full mb-4 cursor-pointer p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 group active:scale-[0.99] transition-all animate-fade-in-up relative overflow-hidden text-left"
          data-testid="ai-prompt-bar"
        >
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/[0.05] blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white/[0.03] blur-2xl pointer-events-none" />
          
          {/* flex-wrap + min-w: on 320 px phones the Preguntar pill drops under the text instead of squeezing it to 3 words a line */}
          <button type="button" className="w-full text-left flex flex-wrap items-center gap-3 sm:gap-4 mb-3 relative z-10">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0 border border-white/10 group-hover:scale-105 transition-transform">
              <Sparkles size={22} className="text-white" />
            </div>
            <div className="flex-1 min-w-[150px]">
              <p className="text-lg sm:text-xl font-extrabold text-white">{'¿Pregunta a Torre\u00A0IA?'}<span className="text-white/80 text-sm font-bold hidden sm:inline"> — tu asistente</span></p>
              <p className="text-[11px] text-white/85 mt-0.5">Tu asistente con los datos de SomosTorre sobre Torrelavega</p>
            </div>
            <span className="ml-auto text-xs font-bold text-indigo-700 bg-white px-4 py-2 rounded-xl shrink-0 group-hover:bg-white/90 transition-colors">
              Preguntar
            </span>
          </button>

          {/* Proactive insight chips */}
          <div className="flex flex-wrap gap-1.5 relative z-10">
            {(() => {
              const h = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' })).getHours();
              const trafficCount = stats?.traffic_incidents;
              const nextBus = gtfsTime(nextBusSantander?.[0]?.departure_torre);
              const currentTemp = weatherObservation?.temperatura;
              const openFarmacias = farmaciasData?.open_count;

              const chips = [];
              if (currentTemp != null) chips.push(`${numEs(currentTemp)}° ahora`);
              if (trafficCount > 0) chips.push(`${trafficCount} incidencias de tráfico`);
              if (nextBus) chips.push(`Bus Santander: ${nextBus}`);
              if (openFarmacias) chips.push(`${openFarmacias} farmacias abiertas`);
              if (h >= 20 || h < 8) chips.push('¿Farmacia de guardia?');
              if (chips.length < 4) chips.push('¿Precio de la luz?');
              if (chips.length < 5 && h < 14) chips.push('¿Próximos trenes?');
              if (chips.length < 5) chips.push('¿Eventos hoy?');

              return chips.slice(0, 5).map((chip, i) => {
                const isQuestion = chip.startsWith('¿');
                const chipClass = 'text-[11px] font-medium text-white bg-white/10 px-2.5 py-1 rounded-lg border border-white/20';
                return isQuestion ? (
                  <button type="button" key={`chip-${i}-${chip.slice(0,10)}`} onClick={(e) => { e.stopPropagation(); window.__torreAskQuestion?.(chip); }}
                    className={`${chipClass} hover:bg-white/20 cursor-pointer`}>
                    {chip}
                  </button>
                ) : (
                  <span key={`chip-${i}-${chip.slice(0,10)}`} className={chipClass}>
                    {chip}
                  </span>
                );
              });
            })()}
          </div>
        </div>

        {/* Quick Actions — the one real launcher on this page, 23 destinations one tap each.
            It used to sit below the sign-up and feedback CTAs, which put a conversion ask in
            front of the shortcuts on a civic page, for an audience that skews older. */}
        <QuickActions navigate={navigate} />
        {/* Tu barrio — act 2, straight under the greeting.
            This was inside the bento grid, below mobility and roughly 3.000px down the page. It
            is the one thing here that no municipal portal does -- what is near YOU, ordered by
            distance -- so burying it among widgets was spending the app's best idea on a slot
            nobody scrolls to. */}
        {placeLocation && (
          <section className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 mb-4 border border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
            data-testid="tu-barrio-section" aria-labelledby="home-tu-barrio">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <MapPin size={17} className="text-torre-teal" aria-hidden="true" />
              <h2 id="home-tu-barrio" className="text-sm sm:text-base font-extrabold text-slate-900">Tu barrio</h2>
            </div>
            <TuBarrioSection
              userLocation={placeLocation}
              approximate={!!placeLocation.approximate}
              setUserLocation={setUserLocation}
              nearestBusStop={nearestBusStop}
              nearestBusArrivals={nearestBusArrivals}
              nearestBike={nearestBike}
              nearestBins={nearestBins}
              nearestSupermarket={nearestSupermarket}
              farmaciaGuardia={farmaciaGuardia}
              farmaciasData={farmaciasData}
              recentIncidencias={recentIncidencias}
              stats={stats}
              navigate={navigate}
              upcoming={upcoming}
              bikeStationCount={bikeStationCount}
              liveTrains={liveTrains}
              nextBusSantander={nextBusSantander}
              user={user}
              onShowAuth={onShowAuth}
              eraLive={eraLive}
            />
          </section>
        )}


        {/* Transparencia — its own act, not a widget buried between residuos and energía.
            Publishing who won which contract and for how much is the most distinctive thing this
            app does, so it gets a section and every figure is its own link to the page that
            explains it. */}
        <section className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 mb-4 border border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
          data-testid="transparencia-section" aria-labelledby="home-transparencia">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <Scale size={17} className="text-torre-teal" aria-hidden="true" />
            <h2 id="home-transparencia" className="text-sm sm:text-base font-extrabold text-slate-900">Transparencia</h2>
          </div>
          {/* Contratos públicos leads, and now looks like it leads.
              It was a thin strip above four postcard-sized stat boxes, so the page gave most of
              this section's weight to "9,4 %" and the least to the one thing no other municipal
              app publishes: who won which contract and for how much. The amount is the headline
              now, the four figures are a compact row beneath it. */}
          <button onClick={() => navigate('/contratacion')}
            className="w-full p-3 sm:p-4 rounded-2xl bg-torre-teal/[0.06] border border-torre-teal/25 hover:border-torre-teal/50 hover:shadow-sm transition-all text-left mb-3"
            data-testid="contratacion-banner">
            <p className="text-[11px] font-bold text-torre-teal uppercase tracking-wide mb-1 flex items-center gap-1"><FileText size={11} className="shrink-0" /> Contratos públicos</p>
            <div className="flex items-start justify-between gap-3 min-w-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-extrabold text-torre-slate leading-snug line-clamp-2">{latestContract ? latestContract.title : 'Contratos y gasto municipal'}</p>
                {latestContract && (
                  <p className="text-[11px] text-slate-500 truncate mt-0.5">{latestContract.status_label}{latestContract.winner_name ? ` → ${latestContract.winner_name}` : ''}</p>
                )}
              </div>
              {/* The figure says what it is. It printed the budget with IVA above «adjudicado»:
                  005/2026 read «394 k€ adjudicado» where PLACSP's award is 108.237,85 € with IVA.
                  The API sends the award («adjudicado») or, for a tender not yet awarded, the
                  budget («presupuesto»), with IVA unless iva_incluido is false (an award known
                  only without IVA, from PLACSP's page); void tenders and unit prices never lead. */}
              {latestContract?.amount > 0 && latestContract?.amount_kind ? (
                <div className="shrink-0 text-right" data-testid="contratacion-banner-amount">
                  <p className="text-xl sm:text-2xl font-extrabold text-torre-teal leading-none">
                    {latestContract.amount >= 1e6 ? `${numEs(latestContract.amount / 1e6, 1, 1)} M€` : latestContract.amount >= 1000 ? `${numEs(Math.round(latestContract.amount / 1000))} k€` : `${numEs(Math.round(latestContract.amount))} €`}
                  </p>
                  <p className="text-[11px] text-slate-400 font-semibold mt-0.5">{latestContract.amount_kind === 'adjudicado' ? 'adjudicado' : 'presupuesto'}{latestContract.iva_incluido === false ? ', sin IVA' : ', IVA incl.'}</p>
                </div>
              ) : (
                <span className="text-[11px] font-bold text-white bg-torre-teal px-2 py-1 rounded-full shrink-0">Ver</span>
              )}
            </div>
          </button>
          <RadiografiaWidget cityStats={cityStats} navigate={navigate} />
          {/* This said "Cuentas, presupuesto y portal de transparencia" and led to /transparencia,
              which holds neither: that page is SomosTorre's own activity — incidencias by barrio,
              by category, by priority, and the neighbours reporting them. The presupuesto figure
              is on Radiografía, one row above. */}
          <button onClick={() => navigate('/transparencia')}
            className="w-full mt-3 py-2 rounded-xl border border-slate-200 text-xs font-bold text-torre-teal hover:border-torre-teal/40 transition-colors"
            data-testid="transparencia-more">
            Qué se reporta en SomosTorre, barrio a barrio
          </button>
        </section>

        {/* Hoy — act 5. Agenda and boletín answered the same question ("what is happening") from
            two separate cards in the grid; one section, two lists. */}
        <section className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 mb-4 border border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
          data-testid="hoy-section" aria-labelledby="home-hoy">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <Calendar size={17} className="text-torre-teal" aria-hidden="true" />
            <h2 id="home-hoy" className="text-sm sm:text-base font-extrabold text-slate-900">Hoy en Torrelavega</h2>
          </div>
          <EstaSemanaSection events={agendaEvents} navigate={navigate} />
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-extrabold text-slate-700 uppercase tracking-wide">Boletín municipal</h3>
              <button onClick={() => navigate('/boletines')} className="text-[11px] font-bold text-torre-teal hover:underline">Ver todos</button>
            </div>
            <BoletinWidget bulletins={allBulletins} navigate={navigate} />
          </div>
        </section>

        {/* Feedback CTA — visible right after hero */}
        <button onClick={() => navigate('/perfil?feedback=open')}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-slate-400 hover:text-torre-teal transition-colors font-medium"
          data-testid="dashboard-feedback-cta">
          <MessageSquarePlus size={12} /> ¿Algo no funciona? Ayúdanos a mejorar
        </button>


        {/* The bento grid is gone. It held Movilidad, Tu Día, Reciclaje, Servicios, Favoritos
            and Comercio -- roughly 500 lines and the bottom two thirds of the page. Every one of
            them has its own page and its own entry in the menu, several fired network requests on
            every load for a card most visitors never scrolled to, and three were opt-in features
            shown to everybody. Nothing was deleted from the app; it was moved to where somebody
            looking for it would go. Favoritos belongs to a person rather than to the city, so it
            goes with the rest of their things under Perfil. */}
      </div>
    </div>
  );
}
